// TilePicker — a reusable, presentational tile palette + hand strip. Click a palette tile to append
// it to the picked hand; click a picked tile to remove that one occurrence. Pure: it owns no state,
// driving everything through { value, onChange } so panels stay the single source of truth. The
// add/remove logic lives in ./tilePicker (unit-tested separately); this file is just the DOM.
import type { TileId } from '@mj/protocol';
import { TileView } from './TileView';
import { PALETTE_TILES, addTile, removeAt } from './tilePickerLogic';

export interface TilePickerProps {
  /** The currently-picked tiles, in order (last is conventionally the winning tile). */
  value: TileId[];
  /** Called with the next list after a palette-add or a hand-strip-remove. */
  onChange: (next: TileId[]) => void;
  /** Optional cap on how many tiles may be picked (e.g. 14 for a standard hand). */
  max?: number;
  /** Tile render size (px width). */
  size?: number;
}

export function TilePicker({ value, onChange, max, size = 30 }: TilePickerProps) {
  const atCapacity = max !== undefined && value.length >= max;

  return (
    <div data-testid="tile-picker">
      {/* Palette: the 34 standard tiles + 3 aka fives. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          padding: 8,
          border: '1px solid #ddd',
          borderRadius: 6,
          background: '#fafaf7',
          opacity: atCapacity ? 0.55 : 1,
        }}
      >
        {PALETTE_TILES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onChange(addTile(value, t, max))}
            disabled={atCapacity}
            title={`add ${t}`}
            aria-label={`add ${t}`}
            data-add={t}
            style={{
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: atCapacity ? 'not-allowed' : 'pointer',
              lineHeight: 0,
            }}
          >
            <TileView tile={t} size={size} />
          </button>
        ))}
      </div>

      {/* Hand strip: the picked tiles; click one to remove it. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          minHeight: Math.round(size * 1.36) + 8,
          marginTop: 8,
          padding: 4,
          border: '1px dashed #bbb',
          borderRadius: 6,
        }}
        data-testid="hand-strip"
      >
        {value.length === 0 ? (
          <span style={{ fontSize: 12, color: '#999', alignSelf: 'center' }}>
            click tiles above to build the hand
          </span>
        ) : (
          value.map((t, i) => (
            <button
              key={`${t}-${i}`}
              type="button"
              onClick={() => onChange(removeAt(value, i))}
              title={`remove ${t}`}
              aria-label={`remove ${t}`}
              data-remove-index={i}
              style={{
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                lineHeight: 0,
              }}
            >
              <TileView tile={t} size={size} />
            </button>
          ))
        )}
      </div>
    </div>
  );
}
